import { MicrositeSessionsState, SessionsState } from './coreAuth.models';
import {
  callMicrositeSessions,
  failMicrositeSessions,
  setMicrositeAuthTokenAuthorizationCode,
  successMicrositeSessions,
  setOptionalData,
  setCustomMultiplier,
  successMicrositeRefreshTokens, clearSession
} from './coreAuth.actions';
import { createReducer, on, Action } from '@ngrx/store';
import {
  createOptionalData,
  createAuthToken,
  createSessionOptionalData
} from './coreAuth.utils';

// Helper type for type-safe property access
type ResponseData = {
  id: number;
  ext_id?: string;
  has_offers?: boolean;
  currency_code?: string;
  country_code?: string;
  auth_tokens?: any;
  freeplays?: number;
  manager_id?: number;
  additional_info?: SessionsState.AdditionalInfo;
  permissions?: SessionsState.Permissions;
  setting?: SessionsState.Setting;
  free_play_session?: SessionsState.FreePlaySession;
  group_tickets_info: SessionsState.GroupTicketInfo;
};

export const initialState: MicrositeSessionsState = {
  id                      : 0,
  ext_id                  : undefined,
  has_offers              : undefined,
  currency_code           : undefined,
  country_code            : undefined,
  auth_tokens             : undefined,
  freeplays               : 0,
  manager_id              : undefined,
  group_tickets_info      : undefined,
  additional_info         : undefined,
  permissions             : undefined,
  setting                 : undefined,
  free_play_session       : undefined,

  isLoading               : false,
  balance                 : undefined,
  user_name               : undefined,
  age_verified            : undefined,
  review_verification     : undefined,
  actionable_promotions_count: undefined,
  period_status           : null,
  currency_multiplier     : null, //1,

  optionalData            : undefined,

  AuthToken : {
    authorization_code: undefined,
    tokens: {
      access_token : undefined,
      refresh_token: undefined,
      expires_at   : undefined
    }
  }
};

// Helper functions moved to coreAuth.utils.ts for better organization and testability

const reducer = createReducer(
  initialState,

  on(callMicrositeSessions, (state) => ({
    ...state,
    isLoading: true
  })),

  on(setOptionalData, (state, { optionalData: data }) => {
    if (!data) return state;

    const newOptionalData = createOptionalData(data);
    return {
      ...state,
      optionalData: { ...state.optionalData, ...newOptionalData }
    };
  }),

  on(successMicrositeSessions, (state, { response }) => {
    if (!response) return state;

    const responseData = response as ResponseData;
    const newOptionalData = createSessionOptionalData(responseData, responseData.currency_code);
    const finalOptionalData = { ...state.optionalData, ...newOptionalData };

    const newState: MicrositeSessionsState = {
      ...state,
      id: responseData.id,
      ext_id: responseData.ext_id,
      has_offers: responseData.has_offers,
      currency_code: responseData.currency_code,
      country_code: responseData.country_code,
      auth_tokens: responseData.auth_tokens,
      freeplays: responseData.freeplays ?? 0,
      manager_id: responseData.manager_id,
      group_tickets_info: responseData.group_tickets_info,
      additional_info: responseData.additional_info,
      permissions: responseData.permissions,
      setting: responseData.setting,
      free_play_session: responseData.free_play_session,

      isLoading: false,
      balance: responseData.additional_info?.balance,
      user_name: responseData.additional_info?.username,
      age_verified: responseData.additional_info?.age_verified,
      review_verification: responseData.additional_info?.review_verification,
      actionable_promotions_count: responseData.free_play_session?.actionable_promotions_count,
      currency_multiplier: !state.currency_multiplier
        ? 1
        : state.currency_multiplier,

      optionalData: finalOptionalData,
      period_status: finalOptionalData.period_status,

      AuthToken: createAuthToken(state.AuthToken.authorization_code, responseData.auth_tokens)
    };

    return newState;
  }),

  on(setCustomMultiplier, (state, { curr_mult }) => ({
    ...state,
    currency_multiplier: curr_mult
  })),

  on(setMicrositeAuthTokenAuthorizationCode, (state, { authorization_code }) => ({
    ...state,
    AuthToken: createAuthToken(authorization_code, state.AuthToken.tokens)
  })),

  on(successMicrositeRefreshTokens, (state, { response }) => ({
    ...state,
    AuthToken: createAuthToken(state.AuthToken.authorization_code, response)
  })),

  on(failMicrositeSessions, (state) => ({
    ...state,
    ext_id: undefined,
    has_offers: undefined,
    currency_code: undefined,
    country_code: undefined,
    auth_tokens: undefined,
    freeplays: 0,
    manager_id: undefined,
    additional_info: undefined,
    permissions: undefined,
    setting: undefined,
    free_play_session: undefined,
    group_tickets_info: undefined,
    optionalData: undefined,
    isLoading: false,
    balance: undefined,
    user_name: undefined,
    age_verified: undefined,
    review_verification: undefined,
    actionable_promotions_count: undefined,
    period_status: null
  })),

  on(clearSession, (state) => ({
    ...state,
    ext_id: undefined,
    has_offers: undefined,
    currency_code: undefined,
    country_code: undefined,
    auth_tokens: undefined,
    freeplays: 0,
    manager_id: undefined,
    additional_info: undefined,
    permissions: undefined,
    setting: undefined,
    free_play_session: undefined,
    group_tickets_info: undefined,
    optionalData: undefined,
    isLoading: false,
    balance: undefined,
    user_name: undefined,
    age_verified: undefined,
    review_verification: undefined,
    actionable_promotions_count: undefined,
    period_status: null
  }))
);

export function micrositeSessionsReducer(
  state: MicrositeSessionsState | undefined,
  action: Action
): MicrositeSessionsState {
  return reducer(state, action);
}

// Business logic moved to coreAuth.utils.ts
